import { useState, type ReactNode } from 'react';
import { ArrowRight, Moon, Sun, Search } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  FormField,
  Input,
  useTheme,
} from '../design';

/**
 * Design system preview — every primitive in every state, both themes.
 * Reachable at /design.
 */
export function DesignPreview() {
  const { theme, setTheme } = useTheme();
  const [demoLoading, setDemoLoading] = useState(false);

  return (
    <div className="min-h-screen bg-canvas text-fg-primary">
      <header className="border-b border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-6 flex items-start justify-between gap-6">
          <div className="space-y-1">
            <p className="text-xs uppercase tracking-wider text-fg-tertiary">
              Sourcito · Design System
            </p>
            <h1 className="text-3xl font-medium">Source smarter. Own your stack.</h1>
            <p className="text-sm text-fg-secondary max-w-prose">
              Phase 1 design foundation — Warm Dark default, terracotta accent, single-origin theme
              switch via CSS variables. Components reference semantic tokens only.
            </p>
            <p className="pt-1">
              <Link
                to="/"
                className="text-sm text-accent-fg hover:underline inline-flex items-center gap-1"
              >
                ← Back to dashboard
              </Link>
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setTheme(theme === 'warm-dark' ? 'warm-light' : 'warm-dark')}
            leadingIcon={
              theme === 'warm-dark' ? <Sun className="size-4" /> : <Moon className="size-4" />
            }
          >
            {theme === 'warm-dark' ? 'Warm Light' : 'Warm Dark'}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-12 space-y-16">
        <Section
          title="Buttons"
          description="Variant × size × state. Loading shows an aria-busy spinner; disabled is the disabled state."
        >
          <div className="space-y-6">
            <Row label="Variant">
              <Button variant="primary">Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
            </Row>
            <Row label="Size">
              <Button size="sm">Small</Button>
              <Button size="md">Medium</Button>
              <Button size="lg">Large</Button>
            </Row>
            <Row label="State">
              <Button>Default</Button>
              <Button disabled>Disabled</Button>
              <Button loading>Loading</Button>
              <Button
                loading={demoLoading}
                onClick={() => {
                  setDemoLoading(true);
                  setTimeout(() => setDemoLoading(false), 1500);
                }}
              >
                Click for loading
              </Button>
            </Row>
            <Row label="With icon">
              <Button leadingIcon={<Search className="size-4" />}>Search products</Button>
              <Button trailingIcon={<ArrowRight className="size-4" />}>Continue</Button>
            </Row>
          </div>
        </Section>

        <Section
          title="Inputs & form fields"
          description="Inputs ship with three sizes. FormField composes label, helper, and error text with proper aria wiring."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
            <FormField label="Email" htmlFor="email" required>
              <Input id="email" type="email" placeholder="you@example.com" autoComplete="email" />
            </FormField>
            <FormField label="Password" htmlFor="password" helper="At least 12 characters.">
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </FormField>
            <FormField
              label="Anthropic API key"
              htmlFor="api-key"
              error="That key didn't validate. Check your dashboard and try again."
            >
              <Input id="api-key" aria-invalid placeholder="sk-ant-..." />
            </FormField>
            <FormField label="Read-only" htmlFor="ro">
              <Input id="ro" disabled value="Not editable" readOnly />
            </FormField>
          </div>
        </Section>

        <Section
          title="Cards"
          description="bg-surface for default, bg-surface-elev for elevated. Borders carry the structure; shadows are restrained."
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Default surface</CardTitle>
                <CardDescription>Sits on the canvas with a subtle border.</CardDescription>
              </CardHeader>
              <p className="text-sm text-fg-secondary">
                Most product UI lives on this surface. Use it for forms, settings panels, and list
                rows.
              </p>
            </Card>
            <Card elevated>
              <CardHeader>
                <CardTitle>Elevated surface</CardTitle>
                <CardDescription>One step up — popovers, modals, command palettes.</CardDescription>
              </CardHeader>
              <p className="text-sm text-fg-secondary">
                Elevation comes from a background-color shift, not a heavy drop shadow. Borders do
                the rest.
              </p>
            </Card>
          </div>
        </Section>

        <Section
          title="Badges"
          description="Semantic variants. The positive / warning / negative trio is purpose-built for ROI score visualization."
        >
          <div className="flex flex-wrap gap-2">
            <Badge>Neutral</Badge>
            <Badge variant="accent">Featured</Badge>
            <Badge variant="positive">+34% ROI</Badge>
            <Badge variant="warning">+18% ROI</Badge>
            <Badge variant="negative">−4% ROI</Badge>
            <Badge variant="info">Pending review</Badge>
          </div>
        </Section>

        <Section
          title="Typography"
          description="Geist Sans, 15px body base, no weight above 600. Geist Mono for ASIN/SKU and any character-aligned data."
        >
          <div className="space-y-3">
            <h1 className="text-4xl font-medium">Display 4xl 36px</h1>
            <h2 className="text-3xl font-medium">Page title 3xl 28px</h2>
            <h3 className="text-2xl font-medium">Card title 2xl 22px</h3>
            <h4 className="text-xl font-medium">Section heading xl 18px</h4>
            <p className="text-base">
              Body base 15px — comfortable for long sessions reading prices, ROI, and SKUs.
            </p>
            <p className="text-sm text-fg-secondary">
              Helper text sm 14px — secondary, slightly muted.
            </p>
            <p className="text-xs text-fg-tertiary">Caption xs 12px — tertiary.</p>
            <p className="font-mono text-base tabular-nums text-fg-primary">
              B0001234567 ASIN $24.99 → $39.95 +59.86%
            </p>
          </div>
        </Section>

        <Section
          title="Color tokens"
          description="The semantic palette in the active theme. Values come from CSS variables that change with the theme class on <html>."
        >
          <Swatches />
        </Section>
      </main>

      <footer className="border-t border-border-subtle">
        <div className="max-w-6xl mx-auto px-8 py-6 text-sm text-fg-secondary flex justify-between items-center">
          <span>
            Active theme: <code className="font-mono text-fg-primary">{theme}</code>
          </span>
          <span className="text-fg-tertiary">
            Phase 1 · v0.0.1 · spec:{' '}
            <code className="font-mono">docs/plans/2026-04-30-design-system-design.md</code>
          </span>
        </div>
      </footer>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section>
      <header className="mb-6">
        <h2 className="text-xl font-medium">{title}</h2>
        {description && <p className="text-sm text-fg-secondary mt-1 max-w-prose">{description}</p>}
      </header>
      {children}
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-4 flex-wrap">
      <span className="text-xs uppercase tracking-wider text-fg-tertiary w-24 shrink-0">
        {label}
      </span>
      <div className="flex items-center gap-3 flex-wrap">{children}</div>
    </div>
  );
}

function Swatches() {
  const swatches: ReadonlyArray<readonly [string, string, string]> = [
    ['bg-canvas', 'bg-canvas border-border-subtle', 'page'],
    ['bg-surface', 'bg-surface border-border-subtle', 'cards, panels'],
    ['bg-surface-elev', 'bg-surface-elev border-border-subtle', 'elevated'],
    ['accent', 'bg-accent', 'primary'],
    ['accent-hover', 'bg-accent-hover', 'hover'],
    ['accent-pressed', 'bg-accent-pressed', 'pressed'],
    ['positive', 'bg-positive', 'high ROI'],
    ['warning', 'bg-warning', 'mid ROI'],
    ['negative', 'bg-negative', 'low ROI'],
    ['info', 'bg-info', 'info'],
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {swatches.map(([name, className, role]) => (
        <div key={name} className="flex flex-col items-stretch gap-1.5">
          <div className={`h-16 rounded-md border ${className}`} />
          <div>
            <code className="text-xs font-mono text-fg-secondary block">{name}</code>
            <span className="text-xs text-fg-tertiary">{role}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
